import React, { useState } from 'react';
import { X, EyeOff, Share2, Package, Clock, TrendingUp, AlertTriangle, ExternalLink, FileText, Zap, CheckCircle2, History, Settings, Loader2, Trash2, Save } from 'lucide-react';
import { formatNumber } from '../utils/calculations';
import { getEnrichedProduct } from '../utils/productEnrichment';
import { useItemHistory } from '../hooks/useItemHistory';
import { useForecastOverrides } from '../hooks/useForecastOverrides';
import HistoryChart from './HistoryChart';

const ItemDetailModal = ({ item, isOpen, onClose, onExclude, isExcluded, isSelected, onToggleSelect }) => {
    const [activeTab, setActiveTab] = useState('details'); // 'details' | 'history'

    if (!isOpen || !item) return null;

    const enrichedData = getEnrichedProduct(item.SKU);

    const generateWhatsAppLink = () => {
        const text = `*סטטוס פריט AEG:*
מק"ט: ${item.SKU}
תיאור: ${enrichedData?.title || item.Description}
מלאי פיזי: ${item.PhysicalStock}
נמל (הזמנות): ${item.OnOrder}
מכירות (חודשי): ${formatNumber(item.Velocity)}
סיווג: ${item.Classification}

*סטטוס*: ${isExcluded ? 'מוחרג' : 'פעיל'}
`;
        return `https://wa.me/?text=${encodeURIComponent(text)}`;
    };

    return (
        <div
            className="fixed inset-0 z-[100] flex items-end md:items-center justify-center bg-black/40 backdrop-blur-sm"
            dir="rtl"
            onClick={onClose}
        >
            <div
                className="bg-white w-full md:max-w-lg md:rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh] md:max-h-[85vh] rounded-t-3xl md:rounded-3xl animate-slide-up"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 p-5 md:p-6 text-white">
                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        className="absolute left-4 top-4 p-2 hover:bg-white/10 rounded-xl transition-colors"
                    >
                        <X size={20} />
                    </button>

                    <div className="flex items-start gap-4">
                        {/* Product Image */}
                        {enrichedData?.imageUrl && (
                            <div className="w-20 h-20 md:w-24 md:h-24 bg-white rounded-2xl flex items-center justify-center p-2 shadow-lg flex-shrink-0">
                                <img
                                    src={enrichedData.imageUrl}
                                    alt={item.SKU}
                                    className="max-w-full max-h-full object-contain"
                                    onError={(e) => e.target.parentElement.style.display = 'none'}
                                />
                            </div>
                        )}

                        <div className="flex-1 min-w-0 pt-1">
                            <p className="text-white/60 text-xs font-medium mb-1">מק״ט</p>
                            <h2 className="text-xl md:text-2xl font-bold tracking-tight">{item.SKU}</h2>
                            <p className="text-white/80 text-sm mt-1 line-clamp-2">
                                {enrichedData?.title || item.Description}
                            </p>
                            {enrichedData?.energyRating && (
                                <span className="inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 bg-emerald-500/20 text-emerald-300 rounded-full text-xs font-semibold">
                                    <Zap size={12} />
                                    דירוג אנרגטי: {enrichedData.energyRating}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200 bg-gray-50">
                    <TabButton
                        active={activeTab === 'details'}
                        onClick={() => setActiveTab('details')}
                        icon={Package}
                        label="פרטים"
                    />
                    <TabButton
                        active={activeTab === 'history'}
                        onClick={() => setActiveTab('history')}
                        icon={History}
                        label="היסטוריה"
                    />
                    <TabButton
                        active={activeTab === 'settings'}
                        onClick={() => setActiveTab('settings')}
                        icon={Settings}
                        label="תחזית"
                    />
                </div>

                {/* Body - Scrollable */}
                <div className="p-5 md:p-6 space-y-5 overflow-y-auto flex-1">
                    {activeTab === 'details' ? (
                        <DetailsTab item={item} enrichedData={enrichedData} />
                    ) : activeTab === 'history' ? (
                        <HistoryTab sku={item.SKU} />
                    ) : (
                        <ForecastSettingsTab sku={item.SKU} />
                    )}
                </div>

                {/* Footer Actions */}
                <div className="p-5 md:p-6 pt-0 space-y-3 border-t border-gray-100 bg-gray-50/50">
                    <div className="flex gap-3">
                        <a
                            href={generateWhatsAppLink()}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white py-3 rounded-xl font-semibold text-center flex items-center justify-center gap-2 transition-colors shadow-lg shadow-emerald-500/20"
                        >
                            <Share2 size={18} />
                            שתף בוואטסאפ
                        </a>

                        <button
                            onClick={() => onExclude(item.SKU)}
                            className={`flex-1 py-3 rounded-xl font-semibold text-center flex items-center justify-center gap-2 transition-colors border ${isExcluded
                                ? 'bg-gray-900 text-white border-gray-900 hover:bg-gray-800'
                                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-100 hover:text-red-600 hover:border-red-200'
                                }`}
                        >
                            <EyeOff size={18} />
                            {isExcluded ? 'בטל החרגה' : 'החרג'}
                        </button>
                    </div>

                    <button
                        onClick={() => onToggleSelect(item.SKU, !isSelected)}
                        className={`w-full py-3.5 rounded-xl font-semibold text-center flex items-center justify-center gap-2 transition-colors border ${isSelected
                            ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                            : 'bg-white text-blue-600 border-blue-200 hover:bg-blue-50'
                            }`}
                    >
                        {isSelected ? <CheckCircle2 size={18} /> : <Package size={18} />}
                        {isSelected ? 'הסר מרשימת הדוח' : 'הוסף לרשימת הדוח'}
                    </button>
                </div>

                {/* Excluded Banner */}
                {isExcluded && (
                    <div className="bg-amber-500 text-amber-950 text-xs text-center py-2 font-medium">
                        פריט זה מוחרג ולא יופיע בחישובים
                    </div>
                )}
            </div>
        </div>
    );
};

// Tab Button Component
const TabButton = ({ active, onClick, icon: Icon, label }) => (
    <button
        onClick={onClick}
        className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold transition-colors ${active
            ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
    >
        <Icon size={16} />
        {label}
    </button>
);

// Details Tab Content
const DetailsTab = ({ item, enrichedData }) => (
    <>
        {/* Short Description */}
        {enrichedData?.shortDescription && (
            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 text-sm text-blue-800 leading-relaxed">
                {enrichedData.shortDescription}
            </div>
        )}

        {/* Key Stats Grid */}
        <div className="grid grid-cols-2 gap-3">
            <StatBox
                icon={Package}
                label="מלאי פיזי"
                value={formatNumber(item.PhysicalStock)}
                highlight={item.PhysicalStock <= 0}
            />
            <StatBox
                icon={Clock}
                label="נמל (הזמנות)"
                value={formatNumber(item.OnOrder)}
            />
            <StatBox
                icon={TrendingUp}
                label="מכירות חודשיות"
                value={formatNumber(item.Velocity)}
            />
            <StatBox
                icon={AlertTriangle}
                label="מוקצה ללקוחות"
                value={formatNumber(item.Allocated)}
            />
        </div>

        {/* Classification Badge */}
        <div className="flex items-center justify-between bg-gray-50 rounded-2xl p-4 border border-gray-100">
            <span className="text-sm text-gray-500">סיווג ABC</span>
            <span className={`px-3 py-1.5 rounded-full text-sm font-bold
                ${item.Classification === 'A' ? 'bg-violet-100 text-violet-700' :
                    item.Classification === 'B' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-200 text-gray-600'}`}>
                Class {item.Classification}
            </span>
        </div>

        {/* Technical Specifications */}
        {enrichedData?.specifications && Object.keys(enrichedData.specifications).length > 0 && (
            <div className="border border-gray-100 rounded-2xl overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 font-semibold text-gray-700 text-sm border-b border-gray-100">
                    מפרט טכני
                </div>
                <div className="divide-y divide-gray-50">
                    {Object.entries(enrichedData.specifications).map(([key, value]) => (
                        <div key={key} className="flex justify-between px-4 py-3 text-sm">
                            <span className="text-gray-500">{key}</span>
                            <span className="font-medium text-gray-900 text-left">{value}</span>
                        </div>
                    ))}
                </div>
            </div>
        )}

        {/* External Links */}
        {enrichedData?.userManualUrl && (
            <a
                href={enrichedData.userManualUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 text-sm text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-xl px-4 py-3 transition-colors"
            >
                <FileText size={16} />
                <span className="flex-1">צפייה במדריך למשתמש</span>
                <ExternalLink size={14} />
            </a>
        )}
    </>
);

// History Tab Content
const HistoryTab = ({ sku }) => {
    const { data, isLoading, error } = useItemHistory(sku, 3);

    return (
        <div className="min-h-[300px]">
            <HistoryChart data={data} isLoading={isLoading} error={error} />
        </div>
    );
};

// Forecast Settings Tab Content
const ForecastSettingsTab = ({ sku }) => {
    const { getOverride, setOverride, deleteOverride, isLoading } = useForecastOverrides();
    const currentOverride = getOverride(sku);

    const [factor, setFactor] = useState(currentOverride?.factor || 1.0);
    const [notes, setNotes] = useState(currentOverride?.notes || '');
    const [expiryDate, setExpiryDate] = useState(currentOverride?.expiryDate || '');
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState(null);

    const handleSave = async () => {
        setIsSaving(true);
        setMessage(null);
        const result = await setOverride(sku, factor, notes || null, expiryDate || null);
        setIsSaving(false);
        if (result.success) {
            setMessage({ type: 'success', text: 'הגדרות נשמרו בהצלחה!' });
        } else {
            setMessage({ type: 'error', text: result.error || 'שגיאה בשמירה' });
        }
    };

    const handleDelete = async () => {
        setIsSaving(true);
        const result = await deleteOverride(sku);
        setIsSaving(false);
        if (result.success) {
            setFactor(1.0);
            setNotes('');
            setExpiryDate('');
            setMessage({ type: 'success', text: 'הדריסה נמחקה' });
        } else {
            setMessage({ type: 'error', text: result.error || 'שגיאה במחיקה' });
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12 text-slate-400">
                <Loader2 className="w-6 h-6 animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-5">
            {/* Current Status */}
            {currentOverride && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
                    <span className="font-semibold">דריסה פעילה:</span> מקדם ×{currentOverride.factor.toFixed(2)}
                    {currentOverride.expiryDate && (
                        <span className="mr-2 text-amber-600">• תפוגה: {currentOverride.expiryDate}</span>
                    )}
                </div>
            )}

            {/* Factor Slider */}
            <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">מקדם תחזית (Safety Factor)</label>
                <div className="flex items-center gap-4">
                    <input
                        type="range"
                        min="0.5"
                        max="2.0"
                        step="0.05"
                        value={factor}
                        onChange={(e) => setFactor(parseFloat(e.target.value))}
                        className="flex-1 accent-blue-600"
                    />
                    <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-lg font-bold text-lg min-w-[70px] text-center">
                        ×{factor.toFixed(2)}
                    </span>
                </div>
                <p className="text-xs text-gray-500">
                    {factor < 1 ? '⚠️ הפחתת ביקוש צפוי' : factor > 1 ? '📈 תוספת ביקוש צפוי (מבצע/עונה)' : 'ללא שינוי'}
                </p>
            </div>

            {/* Notes */}
            <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">הערות (אופציונלי)</label>
                <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="למשל: מבצע בלאק פריידי, לקוח גדול צפוי..."
                    className="w-full p-3 border border-gray-200 rounded-xl text-sm resize-none h-20 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
            </div>

            {/* Expiry Date */}
            <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">תאריך תפוגה (אופציונלי)</label>
                <input
                    type="date"
                    value={expiryDate}
                    onChange={(e) => setExpiryDate(e.target.value)}
                    className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500">הדריסה תתבטל אוטומטית אחרי תאריך זה</p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
                <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                >
                    {isSaving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                    שמור הגדרות
                </button>
                {currentOverride && (
                    <button
                        onClick={handleDelete}
                        disabled={isSaving}
                        className="px-4 py-3 border border-red-200 text-red-600 hover:bg-red-50 rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        <Trash2 size={18} />
                    </button>
                )}
            </div>

            {/* Message */}
            {message && (
                <div className={`text-sm text-center py-2 rounded-lg ${message.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                    {message.text}
                </div>
            )}
        </div>
    );
};

// Helper component for stat boxes
const StatBox = ({ icon: Icon, label, value, highlight = false }) => (
    <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100">
        <div className="flex items-center gap-2 text-gray-400 text-xs mb-1.5">
            <Icon size={14} />
            <span>{label}</span>
        </div>
        <div className={`text-xl font-bold ${highlight ? 'text-red-600' : 'text-gray-900'}`}>
            {value}
        </div>
    </div>
);

export default ItemDetailModal;

